import {
  Box,
  Button,
  Group,
  NumberInput,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { useServiceSelectorFilters } from '@/hooks/useServiceSelectorFilters';
import JurisdictionsSelect from '../JurisdictionsSelect/JurisdictionsSelect';
import ServiceList, { ServiceListProps } from './ServiceList';

const SERVICE_TYPES = [
  'Corporate Services',
  'License obtainment assistance',
  'Document preparation',
  'Legal Services',
  'Account opening assistance',
  'Reselling Agent Services',
  'In-House',
  'Nominee Director and Shareholder',
];

export default function SearchableServiceList({
  onSelect,
  itemRenderer,
  parentServiceId,
  jurisdiction: initialJurisdiction,
  currency: initialCurrency,
  exclude,
  isAdditionalService,
}: ServiceListProps) {
  const { filters, setFilter } = useServiceSelectorFilters();

  const [cursor, setCursor] = useState<number | undefined>();
  const [nextCursor, setNextCursor] = useState(0);

  // Local term input debounced before pushing to URL
  const [termInput, setTermInput] = useState(filters.term ?? '');
  const [debouncedTerm] = useDebouncedValue(termInput, 500);

  // Seed URL from prop defaults on mount when URL is empty
  useEffect(() => {
    if (initialJurisdiction && !filters.jurisdiction) {
      setFilter('jurisdiction', initialJurisdiction);
    }
    if (initialCurrency && !filters.currency) {
      setFilter('currency', initialCurrency);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only seed from prop defaults
  }, []);

  // Push debounced term to URL
  useEffect(() => {
    const next = debouncedTerm || undefined;
    if (next === filters.term) return;
    setFilter('term', next);
    setCursor(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTerm]);

  // Sync local input from URL (navigation, clear)
  useEffect(() => {
    if ((filters.term ?? '') !== termInput) {
      setTermInput(filters.term ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.term]);

  const jurisdiction = filters.jurisdiction ?? initialJurisdiction;
  const currency = filters.currency ?? initialCurrency;
  const strictJurisdiction = filters.strictJurisdiction ?? false;
  const { type, minPrice, maxPrice, serviceFilter } = filters;

  return (
    <>
      <Stack gap="sm" mb="xs">
        <SimpleGrid
          cols={{ base: 1, xs: 2, sm: 3, md: 4, lg: 6 }}
          spacing="sm"
          verticalSpacing="sm"
        >
          <TextInput
            size="xs"
            label="Service"
            placeholder="Search by name"
            value={termInput}
            onChange={(e) => {
              setTermInput(e.target.value);
              setCursor(undefined);
            }}
          />
          <JurisdictionsSelect
            value={jurisdiction}
            onChange={(value) => {
              setFilter('jurisdiction', value);
              setCursor(undefined);
            }}
            strict={strictJurisdiction}
            onStrictChange={(v) => {
              setFilter('strictJurisdiction', v);
              setCursor(undefined);
            }}
          />
          <Select
            label="Type"
            placeholder="All types"
            value={type || null}
            onChange={(value) => {
              setFilter('type', value || undefined);
              setCursor(undefined);
            }}
            data={SERVICE_TYPES}
            clearable
            size="xs"
          />
          <Select
            label="Currency"
            placeholder="All"
            value={currency || null}
            onChange={(value) => {
              setFilter('currency', value || undefined);
              setCursor(undefined);
            }}
            data={['EUR', 'USD', 'GBP']}
            clearable={!initialCurrency}
            disabled={!!initialCurrency}
            size="xs"
          />
          <NumberInput
            label="Min Price"
            placeholder="0"
            value={minPrice ?? ''}
            onChange={(value) => {
              setFilter('minPrice', value ? Number(value) : undefined);
              setCursor(undefined);
            }}
            min={0}
            size="xs"
          />
          <NumberInput
            label="Max Price"
            placeholder="∞"
            value={maxPrice ?? ''}
            onChange={(value) => {
              setFilter('maxPrice', value ? Number(value) : undefined);
              setCursor(undefined);
            }}
            min={0}
            size="xs"
          />
        </SimpleGrid>
        <Box>
          <Text size="xs" fw={500} mb={4}>
            Package Type
          </Text>
          <SegmentedControl
            value={serviceFilter || 'all'}
            onChange={(value) => {
              setFilter(
                'serviceFilter',
                value === 'all'
                  ? undefined
                  : (value as 'packages' | 'standalone'),
              );
              setCursor(undefined);
            }}
            data={[
              { label: 'All', value: 'all' },
              { label: 'Package', value: 'packages' },
              { label: 'Standalone', value: 'standalone' },
            ]}
            size="xs"
          />
        </Box>
      </Stack>
      <Box>
        <ServiceList
          exclude={exclude}
          onSelect={onSelect}
          itemRenderer={itemRenderer}
          parentServiceId={parentServiceId}
          term={debouncedTerm}
          currency={currency}
          jurisdiction={jurisdiction}
          strictJurisdiction={strictJurisdiction}
          type={type}
          minPrice={minPrice}
          maxPrice={maxPrice}
          serviceFilter={serviceFilter}
          isAdditionalService={isAdditionalService}
          cursor={cursor}
          onLoaded={(_, cursor) => {
            setNextCursor(cursor || 0);
          }}
        />
      </Box>
      <Group justify="space-between" align="flex-end">
        {cursor && (
          <Button
            size="xs"
            variant="subtle"
            onClick={() => setCursor(undefined)}
          >
            Back to first page
          </Button>
        )}
        <Box></Box>
        {nextCursor > 0 && (
          <Button
            size="xs"
            variant="subtle"
            onClick={() => setCursor(nextCursor)}
          >
            Next page
          </Button>
        )}
      </Group>
    </>
  );
}
